// Builds the client-side folder tree from the flat folder/document lists
// GET /api/personal/folders/ returns. Kept separate from the matter tree's
// utils/RestructureToDateTree.js since that one groups documents by date
// bucket, a behavior My Files deliberately doesn't need.

export const folderNodeKey = (folder) => `F${folder.id}`;
export const documentNodeKey = (document) => `D${document.id}`;

/**
 * Returns an Ant Design <Tree> `treeData` array rooted at `rootId`, for the
 * left-hand navigation panel. Folders nest as branches; when `documents` is
 * passed, each document appears as a leaf alongside its folder's
 * subfolders (folders first, then documents, both alphabetical) — clicking
 * one opens it the same way clicking its row in the main list does. Every
 * node carries a `type` ("folder" | "document") so callers can tell them
 * apart in onSelect.
 */
export const buildFolderTree = (rootId, folders, documents = []) => {
  const byParentFolders = new Map();
  for (const folder of folders) {
    const parentKey = folder.parent_id || "root";
    if (!byParentFolders.has(parentKey)) byParentFolders.set(parentKey, []);
    byParentFolders.get(parentKey).push(folder);
  }
  const byParentDocuments = new Map();
  for (const document of documents) {
    const parentKey = document.folder_id || "root";
    if (!byParentDocuments.has(parentKey)) byParentDocuments.set(parentKey, []);
    byParentDocuments.get(parentKey).push(document);
  }
  const sortByName = (list) =>
    [...list].sort((a, b) => a.name.localeCompare(b.name));

  const build = (folderId) => {
    const subfolders = sortByName(byParentFolders.get(folderId) || []).map((folder) => ({
      key: folderNodeKey(folder),
      title: folder.name,
      record: folder,
      type: "folder",
      children: build(folder.id),
    }));
    const childDocuments = sortByName(byParentDocuments.get(folderId) || []).map(
      (document) => ({
        key: documentNodeKey(document),
        title: document.name,
        record: document,
        type: "document",
        isLeaf: true,
      })
    );
    return [...subfolders, ...childDocuments];
  };

  const root = folders.find((folder) => folder.is_root && folder.id === rootId) ||
    folders.find((folder) => folder.id === rootId);
  return [
    {
      key: root ? folderNodeKey(root) : "root",
      title: "My Files",
      record: root || { id: rootId, is_root: true, name: "My Files" },
      type: "folder",
      children: build(rootId),
    },
  ];
};

/**
 * Returns { folders, documents } that live directly inside `folderId`
 * (not recursively) — the contents shown in the right-hand file list.
 */
export const childrenOf = (folderId, folders, documents) => ({
  folders: folders
    .filter((folder) => folder.parent_id === folderId)
    .sort((a, b) => a.name.localeCompare(b.name)),
  documents: documents
    .filter((document) => document.folder_id === folderId)
    .sort((a, b) => a.name.localeCompare(b.name)),
});

/**
 * Walks parent_id pointers from `folderId` back to the root, for
 * breadcrumb rendering. Returns folders ordered root-first.
 */
export const breadcrumbTrail = (folderId, folders, rootId) => {
  const byId = new Map(folders.map((folder) => [folder.id, folder]));
  const trail = [];
  let current = byId.get(folderId);
  let guard = 0;
  while (current && guard < 100) {
    trail.unshift(current);
    if (current.id === rootId || !current.parent_id) break;
    current = byId.get(current.parent_id);
    guard += 1;
  }
  return trail;
};
